import { useMutation } from '@tanstack/react-query';
import { ArrowBigUpDash } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  type Project,
  type ProjectMember,
  projectQueries,
  promoteProjectMember,
} from '@/lib/queries/project';
import { generateMutationKey } from '@/lib/queries/utils';

const ActionsCell = ({
  projectId,
  userId,
  role,
  isAdmin,
}: {
  userId: string;
  projectId: string;
  role: string;
  isAdmin?: boolean;
}): JSX.Element => {
  const { mutateAsync, isPending } = useMutation({
    mutationKey: generateMutationKey(
      projectQueries.detail(projectId),
      `promote-${userId}`,
      true,
    ),
    mutationFn: () => promoteProjectMember(projectId, userId),
  });

  if (role === 'admin' || isAdmin !== true) {
    return <TableCell />;
  }

  return (
    <TableCell>
      <TooltipProvider disableHoverableContent>
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <Button
              onClick={() => mutateAsync()}
              disabled={isPending}
              variant="link"
              className="p-0 h-6"
            >
              <ArrowBigUpDash size={16} className="mr-1" />
              Promote to admin
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <FormattedMessage
              id="configuration.projectManagement.promoteTooltip"
              values={{ br: <br /> }}
            />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </TableCell>
  );
};

interface Props {
  members: ProjectMember[];
  isAdmin?: boolean;
  mainProject: Project;
}

export const MembersTable = ({
  members,
  isAdmin,
  mainProject,
}: Props): JSX.Element => {
  return (
    <div className="mt-2 border rounded-md">
      <Table>
        <TableHeader>
          <TableRow className="text-xs">
            <TableHead>
              <FormattedMessage id="configuration.projectManagement.email" />
            </TableHead>
            <TableHead>
              <FormattedMessage id="configuration.projectManagement.role" />
            </TableHead>
            <TableHead>
              <FormattedMessage id="configuration.projectManagement.actions" />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map(member => (
            <TableRow key={member.id} className="text-xs">
              <TableCell>{member.email}</TableCell>
              <TableCell>{member.role}</TableCell>
              <ActionsCell
                projectId={mainProject.id}
                userId={member.id}
                role={member.role}
                isAdmin={isAdmin}
              />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
